import { sendEmail } from '@/utils/emailjs'

/**
 * Dual-write email capture.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * Three forms — the Android waitlist (inline and modal) and the detox
 * challenge — went through EmailJS, which SENDS AN EMAIL and builds no list.
 * There was nothing to send to later, no dedupe, no unsubscribe record and no
 * consent timestamp. Worse, the Gmail OAuth grant behind it has expired more
 * than once, and when it does the signup is lost silently and the visitor is
 * shown an error.
 *
 * ── What changed, and what deliberately did not ─────────────────────────────
 * This writes to BOTH: the Supabase list (durable, deduped, consent-stamped)
 * and EmailJS (the inbox notification that already exists and that somebody
 * may be relying on). Nothing was removed. If the migration is wrong in some
 * way I have not foreseen, the old path is still running underneath it.
 *
 * ── The success contract, which is the real decision here ───────────────────
 * Success is `listed || notified`, not `notified` alone.
 *
 * That is an inversion worth being explicit about. Today, an EmailJS outage
 * means the address is gone AND the visitor sees a failure — the worst of both
 * outcomes. Under this rule, the row is safely on the list and the visitor is
 * correctly told they are signed up, because from their point of view they
 * are. The email to us is a convenience; the list is the product.
 *
 * The two calls are independent and neither can block the other, so a slow or
 * dead EmailJS cannot hold up the database write.
 */

export type CaptureList = 'newsletter' | 'android_waitlist' | 'detox_challenge'

export interface CaptureEmailArgs {
  email: string
  /** What the person consented to. Decides the consent text stored on the row. */
  list: CaptureList
  /** Which surface they signed up on, for attribution. */
  source: string
  /** Optional free-text context — a barcode, a slug, a page path. */
  context?: string
  /** Params for the existing EmailJS notification, passed through untouched. */
  emailParams: Parameters<typeof sendEmail>[0]
}

export interface CaptureEmailResult {
  success: boolean
  /** Made it onto the durable list. */
  listed: boolean
  /** The inbox notification went out. */
  notified: boolean
}

async function addToList(args: CaptureEmailArgs): Promise<boolean> {
  try {
    const res = await fetch('/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: args.email,
        list: args.list,
        source: args.source,
        context: args.context,
      }),
    })
    const data = await res.json().catch(() => null)
    // The route reports an already-present address as ok:true, which is the
    // right answer here too — they are on the list either way.
    return Boolean(data?.ok)
  } catch {
    return false
  }
}

export async function captureEmail(args: CaptureEmailArgs): Promise<CaptureEmailResult> {
  // allSettled, not all: a rejection from either side must not take the other
  // down with it, which is the entire point of writing to two places.
  const [listResult, mailResult] = await Promise.allSettled([
    addToList(args),
    sendEmail(args.emailParams),
  ])

  const listed = listResult.status === 'fulfilled' && listResult.value === true
  const notified = mailResult.status === 'fulfilled' && Boolean(mailResult.value?.success)

  return { success: listed || notified, listed, notified }
}
